import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faCircle, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  subscribeToNotifications,
  markNotificationAsRead,
  markNotificationAsUnread,
  markAllNotificationsAsRead,
} from '../../services/notificationService';
import { formatNotificationDate, replaceRouteParams } from '../../utils/notificationUtils';
import type { Notification } from '../../types/notification';
import './NotificationBell.css';

interface NotificationWithId extends Notification {
  id: string;
}

export default function NotificationBell() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationWithId[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Group notifications by source
  const userNotifications = notifications
    .filter((n) => n.source === 'user')
    .slice(0, 10);
  const orgNotifications = notifications
    .filter((n) => n.source === 'organization')
    .slice(0, 10);

  // Calculate unread count
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Subscribe to notifications
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      return;
    }

    let unsubscribe: (() => void) | null = null;

    try {
      unsubscribe = subscribeToNotifications(
        currentUser.uid,
        (notifs) => {
          setNotifications(notifs);
        },
        (error) => {
          // Gracefully handle permission errors and other subscription errors
          console.error('Error loading notifications:', error);
          // Set empty notifications list on error rather than crashing
          setNotifications([]);
        }
      );
    } catch (error) {
      console.error('Failed to subscribe to notifications:', error);
      setNotifications([]);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const handleNotificationClick = async (notification: NotificationWithId) => {
    if (!currentUser) return;

    try {
      // Mark as read if not already
      if (!notification.read) {
        await markNotificationAsRead(currentUser.uid, notification.id);
      }

      // Navigate to the link
      if (typeof notification.link === 'string') {
        if (notification.link.startsWith('http')) {
          // Absolute URL - open in new tab
          window.open(notification.link, '_blank', 'noopener,noreferrer');
        } else {
          // Relative URL - navigate
          navigate(notification.link);
          setShowDropdown(false);
        }
      } else if (notification.link.type === 'route') {
        // Named route - construct path and navigate
        const path = notification.link.params
          ? replaceRouteParams(notification.link.name, notification.link.params)
          : notification.link.name;
        navigate(path);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleToggleRead = async (
    e: React.MouseEvent,
    notification: NotificationWithId
  ) => {
    e.stopPropagation();
    if (!currentUser) return;

    try {
      if (notification.read) {
        await markNotificationAsUnread(currentUser.uid, notification.id);
      } else {
        await markNotificationAsRead(currentUser.uid, notification.id);
      }
    } catch (error) {
      console.error('Error toggling notification read state:', error);
    }
  };

  const handleMarkAllRead = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      await markAllNotificationsAsRead(currentUser.uid);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderNotification = (notification: NotificationWithId) => (
    <div
      key={notification.id}
      className={`notification-item ${!notification.read ? 'unread' : ''}`}
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="notification-content">
        <div className="notification-header">
          <span className="notification-title">{notification.title}</span>
          {!notification.read && (
            <FontAwesomeIcon icon={faCircle} className="unread-indicator" />
          )}
        </div>
        <p className="notification-body">{notification.body}</p>
        <div className="notification-footer">
          <span className="notification-time">
            {formatNotificationDate(notification.createdAt)}
          </span>
          <button
            type="button"
            className="toggle-read-button"
            onClick={(e) => handleToggleRead(e, notification)}
            title={notification.read ? 'Mark as unread' : 'Mark as read'}
          >
            <FontAwesomeIcon icon={faCheck} />
          </button>
        </div>
      </div>
    </div>
  );

  if (!currentUser) {
    return null;
  }

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        type="button"
        className="notification-bell-button"
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label="Notifications"
      >
        <FontAwesomeIcon icon={faBell} className="bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                className="mark-all-read-button"
                onClick={handleMarkAllRead}
                disabled={loading}
              >
                {loading ? 'Marking...' : 'Mark All Read'}
              </button>
            )}
          </div>

          <div className="notification-dropdown-body">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <p>No notifications</p>
              </div>
            ) : (
              <>
                {userNotifications.length > 0 && (
                  <div className="notification-group">
                    <h4 className="notification-group-title">
                      User Notifications
                    </h4>
                    {userNotifications.map(renderNotification)}
                  </div>
                )}

                {orgNotifications.length > 0 && (
                  <div className="notification-group">
                    <h4 className="notification-group-title">
                      Organization Notifications
                    </h4>
                    {orgNotifications.map(renderNotification)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
